import { type ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useParams } from "react-router-dom";
import {
  ArrowLeft,
  CheckCircle2,
  ChevronDown,
  Images,
  Loader2,
  MapPin,
  Pencil,
  Play,
  Reply,
  RotateCcw,
  Send,
  ThumbsDown,
  ThumbsUp,
  Trash2,
  X,
} from "lucide-react";
import { format, isToday, isYesterday } from "date-fns";
import { th } from "date-fns/locale";
import { PageContainer } from "@/components/layout/PageContainer";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { usePageTranslation } from "@/i18n/useTranslation";
import { useLanguage } from "@/i18n/useLanguage";
import { supportTranslations as enSupport } from "@/i18n/locales/en/support";
import { supportTranslations as thSupport } from "@/i18n/locales/th/support";
import {
  addReply,
  getTicket,
  getIncomingTicket,
  isSupportReceiver,
  setSatisfaction,
  setStatus,
  setPriority,
  setCategory,
  setInternalStatus,
  setExternalStatus,
  addAgentReply,
  editAgentMessage,
  deleteMessage,
  sendAgentDraft,
  discardAgentDraft,
} from "./api/supportApi";
import { useAuthStore } from "@/lib/store/authStore";
import { INTERNAL_STATUSES, TICKET_CATEGORIES, TICKET_PRIORITIES, TICKET_STATUSES } from "./types";

// Thai labels for the private answering-side pipeline (customer never sees these).
const INTERNAL_STATUS_TH: Record<string, string> = {
  New: "ใหม่",
  Investigating: "กำลังตรวจสอบ",
  Fixing: "กำลังแก้ไข",
  "Draft Ready": "ร่างพร้อมอนุมัติ",
  Approved: "อนุมัติ/ส่งแล้ว",
  Waiting: "รอลูกค้า",
  Done: "เสร็จ",
};
import { RichComposer } from "./components/RichComposer";
import { ImageLightbox } from "./components/ImageLightbox";
import { KnowledgeBaseSuggestions } from "./components/KnowledgeBaseSuggestions";
import { EntityPreview } from "./components/EntityPreview";
import {
  StatusPill,
  categoryLabel,
  priorityLabel,
  relativeTime,
  statusLabel,
} from "./components/meta";
import { sanitizeHtml, resolveMediaUrls, splitMessageMedia } from "./components/sanitize";
import type { Ticket, TicketCategory, TicketMessage, TicketPriority } from "./types";

type T = typeof enSupport;
type SendVars = { text: string; html?: string; replyTo?: TicketMessage["replyTo"] };

const EST_RESPONSE_MINS = 5;

function snippetOf(m: TicketMessage): string {
  const text = m.body || (m.bodyHtml ?? "").replace(/<[^>]+>/g, " ");
  return text.trim().slice(0, 80);
}

function dayLabel(iso: string, t: T, language: string): string {
  const d = new Date(iso);
  if (isToday(d)) return t.dateToday;
  if (isYesterday(d)) return t.dateYesterday;
  return format(d, "d MMM yyyy", { locale: language === "th" ? th : undefined });
}

export function SupportTicketDetailPage() {
  const { ticketNo = "" } = useParams<{ ticketNo: string }>();
  const queryClient = useQueryClient();
  const { language } = useLanguage();
  const t = usePageTranslation({ en: enSupport, th: thSupport }) as T;

  const [replyHtml, setReplyHtml] = useState("");
  const [replyText, setReplyText] = useState("");
  const [composerKey, setComposerKey] = useState(0);
  const [replyingTo, setReplyingTo] = useState<TicketMessage | null>(null);
  // Receiver-only (john) message moderation: inline edit of an agent message.
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editHtml, setEditHtml] = useState("");
  const [editText, setEditText] = useState("");
  const [lightboxSrc, setLightboxSrc] = useState<string | null>(null);
  const [agentTyping, setAgentTyping] = useState(false);
  const [seen, setSeen] = useState(false);
  // "Reported from" context is collapsed by default so the sticky header stays compact.
  const [contextOpen, setContextOpen] = useState(false);
  const threadEndRef = useRef<HTMLDivElement>(null);

  // Support owner (john) reads ANY store's ticket via the cross-tenant reader;
  // everyone else uses the tenant-isolated getTicket. Reply controls below are
  // hidden for the owner (drafts/approval land in Step 2).
  const username = useAuthStore((s) => s.username);
  const isReceiver = isSupportReceiver(username);

  const { data: ticket, isLoading } = useQuery({
    queryKey: ["support", "ticket", ticketNo],
    queryFn: () => (isReceiver ? getIncomingTicket(ticketNo) : getTicket(ticketNo)),
    enabled: !!ticketNo,
    refetchInterval: 5000,
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ["support", "ticket", ticketNo] });
    queryClient.invalidateQueries({ queryKey: ["support", "tickets"] });
  };

  const replyMutation = useMutation({
    mutationFn: (vars: SendVars) => addReply(ticketNo, vars.text, vars.html, vars.replyTo),
    onMutate: async (vars) => {
      await queryClient.cancelQueries({ queryKey: ["support", "ticket", ticketNo] });
      const prev = queryClient.getQueryData<Ticket>(["support", "ticket", ticketNo]);
      const tempId = `tmp-${Date.now()}`;
      const optimistic: TicketMessage = {
        id: tempId,
        senderType: "customer",
        senderName: prev?.raisedByName ?? "You",
        body: vars.text,
        bodyHtml: vars.html,
        replyTo: vars.replyTo,
        sentOn: new Date().toISOString(),
        pending: true,
      };
      queryClient.setQueryData<Ticket>(["support", "ticket", ticketNo], (old) =>
        old ? { ...old, messages: [...old.messages, optimistic] } : old,
      );
      return { tempId };
    },
    onError: (_e, _v, ctx) => {
      queryClient.setQueryData<Ticket>(["support", "ticket", ticketNo], (old) =>
        old
          ? {
              ...old,
              messages: old.messages.map((m) =>
                m.id === ctx?.tempId ? { ...m, pending: false, failed: true } : m,
              ),
            }
          : old,
      );
    },
    onSuccess: () => {
      setReplyHtml("");
      setReplyText("");
      setReplyingTo(null);
      setComposerKey((k) => k + 1);
      setAgentTyping(true);
      window.setTimeout(() => setAgentTyping(false), 4000);
      invalidate();
    },
  });

  const statusMutation = useMutation({
    mutationFn: (next: "Resolved" | "Open") => setStatus(ticketNo, next),
    onSuccess: invalidate,
  });
  // Priority + type (category) triage — editable from the sidebar. Routes to the
  // agent endpoint for the receiver (john, cross-tenant) or the tenant-scoped
  // update for the customer editing their own ticket.
  const priorityMutation = useMutation({
    mutationFn: (next: TicketPriority) => setPriority(ticketNo, next, isReceiver),
    onSuccess: invalidate,
  });
  const categoryMutation = useMutation({
    mutationFn: (next: TicketCategory) => setCategory(ticketNo, next, isReceiver),
    onSuccess: invalidate,
  });
  // Agent inbox (john) status controls — internal (private) + external (customer-facing).
  const internalStatusMutation = useMutation({
    mutationFn: (next: string) => setInternalStatus(ticketNo, next),
    onSuccess: invalidate,
  });
  const externalStatusMutation = useMutation({
    mutationFn: (next: string) => setExternalStatus(ticketNo, next),
    onSuccess: invalidate,
  });
  // Reply AS the Zaviago Support Team (agent voice) — receiver inbox only.
  const agentReplyMutation = useMutation({
    mutationFn: (vars: { text: string; html?: string }) =>
      addAgentReply(ticketNo, vars.text, vars.html),
    onSuccess: () => {
      setReplyHtml("");
      setReplyText("");
      setComposerKey((k) => k + 1);
      invalidate();
    },
  });
  // Agent draft (john inbox): send it to the customer, or discard it. The draft
  // itself is prepared elsewhere; here john reviews it and decides.
  const sendDraftMutation = useMutation({
    mutationFn: () => sendAgentDraft(ticketNo),
    onSuccess: invalidate,
  });
  const discardDraftMutation = useMutation({
    mutationFn: () => discardAgentDraft(ticketNo),
    onSuccess: invalidate,
  });
  const satisfactionMutation = useMutation({
    mutationFn: (rating: "up" | "down") => setSatisfaction(ticketNo, rating),
    onSuccess: invalidate,
  });
  // Receiver (john) moderation: edit an agent message, delete any message.
  const editMessageMutation = useMutation({
    mutationFn: (vars: { messageId: string; text: string; html?: string }) =>
      editAgentMessage(ticketNo, vars.messageId, vars.text, vars.html),
    onSuccess: () => {
      setEditingId(null);
      setEditHtml("");
      setEditText("");
      invalidate();
    },
  });
  const deleteMessageMutation = useMutation({
    mutationFn: (messageId: string) => deleteMessage(ticketNo, messageId),
    onSuccess: invalidate,
  });

  const startEdit = (m: TicketMessage) => {
    setEditingId(m.id);
    setEditHtml(m.bodyHtml ?? "");
    setEditText(m.body ?? "");
  };
  const cancelEdit = () => {
    setEditingId(null);
    setEditHtml("");
    setEditText("");
  };
  const saveEdit = () => {
    if (!editingId || !editText.trim() || editMessageMutation.isPending) return;
    editMessageMutation.mutate({
      messageId: editingId,
      text: editText,
      html: editHtml || undefined,
    });
  };
  const confirmDelete = (m: TicketMessage) => {
    if (deleteMessageMutation.isPending) return;
    if (window.confirm("ลบข้อความนี้ออกจากแชท? การลบไม่สามารถย้อนกลับได้")) {
      deleteMessageMutation.mutate(m.id);
    }
  };

  const messages = ticket?.messages ?? [];

  // All images/videos across the conversation, for the sidebar media gallery —
  // extracted from each message's (sanitized, URL-resolved) bodyHtml, de-duped,
  // and tagged by who sent it (customer vs Zaviago team) so the gallery groups.
  const mediaItems = useMemo<MediaItem[]>(() => {
    if (typeof window === "undefined") return [];
    const items: MediaItem[] = [];
    const seen = new Set<string>();
    for (const m of messages) {
      if (!m.bodyHtml) continue;
      const from: MediaItem["from"] = m.senderType === "agent" ? "agent" : "customer";
      const doc = new DOMParser().parseFromString(
        sanitizeHtml(resolveMediaUrls(m.bodyHtml)),
        "text/html",
      );
      doc.querySelectorAll("img, video").forEach((el) => {
        const isVideo = el.tagName === "VIDEO";
        const src =
          el.getAttribute("src") ||
          el.querySelector("source")?.getAttribute("src") ||
          "";
        if (src && !seen.has(src)) {
          seen.add(src);
          items.push({ src, kind: isVideo ? "video" : "image", from });
        }
      });
    }
    return items;
  }, [messages]);
  const lastMessage = messages[messages.length - 1];

  useEffect(() => {
    threadEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages.length, agentTyping]);

  // Simulated "seen by our team" a couple seconds after the customer's last message.
  useEffect(() => {
    setSeen(false);
    if (lastMessage?.senderType === "customer" && !lastMessage.pending && !lastMessage.failed) {
      const id = window.setTimeout(() => setSeen(true), 2500);
      return () => window.clearTimeout(id);
    }
  }, [lastMessage?.id, lastMessage?.pending, lastMessage?.failed]);

  const canSend = replyText.trim().length > 0 && !replyMutation.isPending;
  const isClosed = ticket?.status === "Resolved" || ticket?.status === "Closed";

  const sendRich = () => {
    if (!canSend) return;
    replyMutation.mutate({
      text: replyText,
      html: replyHtml || undefined,
      replyTo: replyingTo
        ? { id: replyingTo.id, senderType: replyingTo.senderType, snippet: snippetOf(replyingTo) }
        : undefined,
    });
  };
  const sendQuick = (text: string) => {
    if (!replyMutation.isPending) replyMutation.mutate({ text });
  };
  const retry = (m: TicketMessage) => {
    queryClient.setQueryData<Ticket>(["support", "ticket", ticketNo], (old) =>
      old ? { ...old, messages: old.messages.filter((x) => x.id !== m.id) } : old,
    );
    replyMutation.mutate({ text: m.body, html: m.bodyHtml, replyTo: m.replyTo });
  };

  const backLink = (
    <Link
      to="/support"
      className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-500 transition-colors hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100"
    >
      <ArrowLeft className="h-4 w-4" />
      {t.backToSupport}
    </Link>
  );

  return (
    <div className="flex min-h-screen flex-col bg-white dark:bg-gray-950">
      {/* Full-height flex chain so the two-column row — and the self-stretch
          sidebar's border-l — always fills the viewport even on a short chat. */}
      <PageContainer fullWidth className="flex flex-1 flex-col !pt-0 !pb-0">
        {isLoading ? (
          <div className="space-y-4 pt-4 md:pt-6">
            {backLink}
            <Skeleton className="h-24 w-full rounded-2xl" />
            <Skeleton className="h-16 w-2/3 rounded-2xl" />
            <Skeleton className="ml-auto h-16 w-2/3 rounded-2xl" />
          </div>
        ) : !ticket ? (
          <div className="pt-4 md:pt-6">
            {backLink}
            <div className="mt-10 text-center text-gray-500 dark:text-gray-400">
              <p>{t.notFound}</p>
              <Button asChild variant="outline" className="mt-4 rounded-full">
                <Link to="/support">{t.backToSupport}</Link>
              </Button>
            </div>
          </div>
        ) : (
          <>
            {/* Back link spans the top; below it the conversation sits on the
                left with the ticket header/meta as a sticky right sidebar
                (stacks on top on mobile). */}
            {/* Back link sits at the very top on mobile; on desktop it lives in
                the left column so the bordered sidebar can run flush to the top. */}
            <div className="pt-3 lg:hidden">{backLink}</div>
            {/* lg:!mt-0 cancels the space-y-6 gap the hidden mobile back link
                above would otherwise force onto this row on desktop. */}
            <div className="flex-1 lg:flex lg:items-start lg:gap-6 lg:!mt-0">
              {/* Ticket header & meta — full-height left-bordered right sidebar on
                  desktop; a bordered block on top on mobile. */}
              <aside className="lg:order-2 lg:-mr-6 lg:w-[312px] lg:shrink-0 lg:self-stretch lg:border-l lg:border-border">
                <div className="pt-3 lg:sticky lg:top-0 lg:pl-6 lg:pr-6 lg:pt-3">
                  {/* Ticket header/meta — a full-height left-bordered sidebar panel
                      on desktop (not a floating card); a bordered block on mobile. */}
                  <div className="border-b border-gray-200 pb-5 dark:border-gray-800 lg:border-b-0 lg:pb-0">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <h1 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                    {ticket.subject}
                  </h1>
                  <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-400 dark:text-gray-500">
                    <span className="font-mono">{ticket.name}</span>
                    <span className="text-gray-300 dark:text-gray-600">·</span>
                    <span>{t.openedAgo.replace("{{time}}", relativeTime(ticket.createdOn, language))}</span>
                  </div>
                </div>
                {/* Status + action stack on separate rows so they stay readable
                    in the narrow sidebar (badge on top, full-width action below). */}
                <div className="flex w-full flex-col items-start gap-2">
                  <StatusPill status={ticket.status} label={statusLabel(t, ticket.status)} />
                  {/* Receiver (john) uses the internal/external status dropdowns below;
                      these tenant-scoped quick buttons are for the customer view only. */}
                  {!isReceiver && (isClosed ? (
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full justify-center rounded-full"
                      onClick={() => statusMutation.mutate("Open")}
                      disabled={statusMutation.isPending}
                    >
                      <RotateCcw className="h-3.5 w-3.5" />
                      {t.reopen}
                    </Button>
                  ) : (
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full justify-center rounded-full"
                      onClick={() => statusMutation.mutate("Resolved")}
                      disabled={statusMutation.isPending}
                    >
                      <CheckCircle2 className="h-3.5 w-3.5" />
                      {t.markResolved}
                    </Button>
                  ))}
                </div>
              </div>

              {/* Editable triage — type (category) + priority as dropdowns.
                  appearance-none + a custom chevron so the arrow isn't jammed
                  against the rounded edge like the native control was. */}
              <div className="mt-4 grid grid-cols-2 gap-2 border-t border-gray-100 pt-4 dark:border-gray-800">
                <label className="flex min-w-0 flex-col gap-1">
                  <span className="text-[11px] font-medium text-gray-500 dark:text-gray-400">ประเภท</span>
                  <div className="relative">
                    <select
                      value={ticket.category}
                      disabled={categoryMutation.isPending}
                      onChange={(e) => categoryMutation.mutate(e.target.value as TicketCategory)}
                      className="w-full appearance-none truncate rounded-lg border border-gray-200 bg-white py-1.5 pl-2.5 pr-7 text-xs text-gray-900 shadow-sm focus:border-blue-400 focus:outline-none disabled:opacity-50 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100"
                    >
                      {TICKET_CATEGORIES.map((c) => (
                        <option key={c} value={c}>
                          {categoryLabel(t, c)}
                        </option>
                      ))}
                    </select>
                    <ChevronDown className="pointer-events-none absolute right-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
                  </div>
                </label>
                <label className="flex min-w-0 flex-col gap-1">
                  <span className="text-[11px] font-medium text-gray-500 dark:text-gray-400">ความสำคัญ</span>
                  <div className="relative">
                    <select
                      value={ticket.priority}
                      disabled={priorityMutation.isPending}
                      onChange={(e) => priorityMutation.mutate(e.target.value as TicketPriority)}
                      className="w-full appearance-none truncate rounded-lg border border-gray-200 bg-white py-1.5 pl-2.5 pr-7 text-xs text-gray-900 shadow-sm focus:border-blue-400 focus:outline-none disabled:opacity-50 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100"
                    >
                      {TICKET_PRIORITIES.map((p) => (
                        <option key={p} value={p}>
                          {priorityLabel(t, p)}
                        </option>
                      ))}
                    </select>
                    <ChevronDown className="pointer-events-none absolute right-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
                  </div>
                </label>
              </div>

              {ticket.context && (
                <div className="mt-4 border-t border-gray-100 pt-4 dark:border-gray-800">
                  <button
                    type="button"
                    onClick={() => setContextOpen((o) => !o)}
                    aria-expanded={contextOpen}
                    className="flex w-full items-center gap-1.5 text-xs font-medium text-gray-500 transition-colors hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                  >
                    <MapPin className="h-3.5 w-3.5 shrink-0 text-primary" />
                    {t.contextReportedFrom}
                    <ChevronDown
                      className={cn(
                        "ml-auto h-3.5 w-3.5 shrink-0 text-gray-400 transition-transform duration-200",
                        contextOpen && "rotate-180",
                      )}
                    />
                  </button>
                  {contextOpen && (
                    <div className="mt-2">
                      {ticket.context.entity ? (
                        <EntityPreview
                          entity={ticket.context.entity}
                          url={ticket.context.url}
                          t={t}
                          size="sm"
                        />
                      ) : (
                        <p className="truncate font-mono text-xs text-gray-400">{ticket.context.url}</p>
                      )}
                      {ticket.context.sessionId && (
                        <p className="mt-1.5 truncate font-mono text-[11px] text-gray-400 dark:text-gray-500">
                          session: {ticket.context.sessionId}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )}
                  </div>
                  {/* Media gallery — every image/video in the ticket, one tap to preview */}
                  <TicketMediaGallery items={mediaItems} onOpenImage={setLightboxSrc} />
                </div>
              </aside>

              {/* Main column: back link (desktop) + conversation + composer */}
              <div className="lg:order-1 lg:min-w-0 lg:flex-1">
            <div className="hidden bg-white pt-3 pb-3 dark:bg-gray-950 lg:sticky lg:top-0 lg:z-10 lg:block">{backLink}</div>
            {/* Conversation */}
            <div className="mt-4 space-y-4">
              <SystemPill>
                {t.openedAgo.replace("{{time}}", relativeTime(ticket.createdOn, language))}
              </SystemPill>

              {messages.map((m, i) => {
                const prev = messages[i - 1];
                const showDate = !prev || dayLabel(prev.sentOn, t, language) !== dayLabel(m.sentOn, t, language);
                const isLast = i === messages.length - 1;
                const isEditing = isReceiver && editingId === m.id;
                return (
                  <div key={m.id} className="space-y-4">
                    {showDate && <SystemPill>{dayLabel(m.sentOn, t, language)}</SystemPill>}
                    {isEditing ? (
                      <MessageEditor
                        message={m}
                        onChange={(html, text) => {
                          setEditHtml(html);
                          setEditText(text);
                        }}
                        onSave={saveEdit}
                        onCancel={cancelEdit}
                        canSave={!!editText.trim()}
                        saving={editMessageMutation.isPending}
                      />
                    ) : (
                      <MessageBubble
                        message={m}
                        t={t}
                        language={language}
                        onReply={() => setReplyingTo(m)}
                        onImageClick={setLightboxSrc}
                        canModerate={isReceiver}
                        onEdit={() => startEdit(m)}
                        onDelete={() => confirmDelete(m)}
                        deleting={deleteMessageMutation.isPending}
                      />
                    )}
                    {isLast && m.senderType === "customer" && (
                      <div className="flex justify-end px-1 -mt-2">
                        {m.pending ? (
                          <span className="text-[11px] text-gray-400">{t.sending}</span>
                        ) : m.failed ? (
                          <button
                            onClick={() => retry(m)}
                            className="inline-flex items-center gap-1 text-[11px] font-medium text-red-500 hover:underline"
                          >
                            {t.sendFailed} · {t.retry}
                          </button>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-[11px] text-gray-400 dark:text-gray-500">
                            <CheckCircle2 className="h-3 w-3" />
                            {seen ? t.seen : t.sent}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}

              {agentTyping && <TypingIndicator label={t.agentTyping} />}

              {isClosed && (
                <SystemPill tone="success">
                  <CheckCircle2 className="h-3.5 w-3.5" />
                  {t.timelineResolved}
                </SystemPill>
              )}
              <div ref={threadEndRef} />
            </div>

            {/* CSAT after resolve */}
            {isClosed && (
              <div className="mt-4">
                {ticket.satisfaction ? (
                  <div className="flex items-center justify-center gap-2 rounded-2xl border border-emerald-100 bg-emerald-50/60 py-3 text-sm font-medium text-emerald-700 dark:border-emerald-500/20 dark:bg-emerald-500/[0.06] dark:text-emerald-300">
                    <CheckCircle2 className="h-4 w-4" />
                    {t.csatThanks}
                  </div>
                ) : (
                  <div className="flex flex-col items-center gap-3 rounded-2xl border border-gray-200 bg-white py-4 dark:border-gray-800 dark:bg-gray-900/40 sm:flex-row sm:justify-center">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-200">
                      {t.csatQuestion}
                    </span>
                    <div className="flex items-center gap-2">
                      <Button variant="outline" size="sm" className="rounded-full" onClick={() => satisfactionMutation.mutate("up")} disabled={satisfactionMutation.isPending}>
                        <ThumbsUp className="h-3.5 w-3.5" />
                        {t.csatYes}
                      </Button>
                      <Button variant="outline" size="sm" className="rounded-full" onClick={() => satisfactionMutation.mutate("down")} disabled={satisfactionMutation.isPending}>
                        <ThumbsDown className="h-3.5 w-3.5" />
                        {t.csatNo}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Related help articles while you wait — customer view only */}
            {!isClosed && !isReceiver && (
              <div className="mt-4">
                <KnowledgeBaseSuggestions query={ticket.subject} />
              </div>
            )}

            {/* Reply composer — hidden for the support owner (john), who will
                approve/edit AI drafts (Step 2) instead of typing a customer reply. */}
            {isReceiver ? (
              <div className="sticky bottom-0 mt-4 space-y-3 rounded-2xl border border-blue-200 bg-blue-50/50 p-4 dark:border-blue-500/30 dark:bg-blue-500/5">
                <p className="text-xs font-medium text-blue-700 dark:text-blue-300">
                  มุมมองทีมงาน Zaviago (Support){ticket.tenantName ? ` · ${ticket.tenantName}` : ""}
                </p>
                {/* Agent-only unsent DRAFT — never visible to the customer. Review then Send or Discard. */}
                {ticket.agentDraft && (
                  <div className="rounded-xl border border-amber-300 bg-amber-50 p-3 dark:border-amber-500/40 dark:bg-amber-500/10">
                    <div className="mb-2 flex items-center justify-between gap-2">
                      <span className="text-xs font-semibold text-amber-800 dark:text-amber-200">
                        ✎ ฉบับร่าง (ยังไม่ส่ง) · ลูกค้ายังไม่เห็น
                      </span>
                      {ticket.agentDraft.updatedOn && (
                        <span className="text-[11px] text-amber-700/70 dark:text-amber-300/60">
                          ร่างเมื่อ {new Date(ticket.agentDraft.updatedOn).toLocaleString("th-TH")}
                        </span>
                      )}
                    </div>
                    <div
                      className="max-h-[420px] overflow-y-auto rounded-lg bg-white p-3 text-sm leading-relaxed text-gray-800 dark:bg-gray-900 dark:text-gray-100 [&_img]:my-2 [&_img]:max-w-full [&_img]:rounded-lg [&_p]:mb-2"
                      dangerouslySetInnerHTML={{ __html: ticket.agentDraft.bodyHtml ?? "" }}
                    />
                    <div className="mt-3 flex items-center justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="rounded-full text-gray-500"
                        disabled={discardDraftMutation.isPending || sendDraftMutation.isPending}
                        onClick={() => discardDraftMutation.mutate()}
                      >
                        {discardDraftMutation.isPending && (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        )}
                        ลบฉบับร่าง
                      </Button>
                      <Button
                        size="sm"
                        className="rounded-full"
                        disabled={sendDraftMutation.isPending || discardDraftMutation.isPending}
                        onClick={() => sendDraftMutation.mutate()}
                      >
                        {sendDraftMutation.isPending ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Send className="h-4 w-4" />
                        )}
                        ส่งฉบับร่างนี้ให้ลูกค้า
                      </Button>
                    </div>
                  </div>
                )}
                <div className="grid gap-3 sm:grid-cols-2">
                  {/* Internal (private) status — customer never sees this */}
                  <label className="flex flex-col gap-1">
                    <span className="text-xs font-medium text-gray-600 dark:text-gray-400">
                      สถานะภายใน (ทีม/AI) — ลูกค้าไม่เห็น
                    </span>
                    <select
                      value={ticket.internalStatus ?? "New"}
                      disabled={internalStatusMutation.isPending}
                      onChange={(e) => internalStatusMutation.mutate(e.target.value)}
                      className="rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-blue-400 focus:outline-none disabled:opacity-50 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100"
                    >
                      {INTERNAL_STATUSES.map((s) => (
                        <option key={s} value={s}>
                          {INTERNAL_STATUS_TH[s] ?? s}
                        </option>
                      ))}
                    </select>
                  </label>
                  {/* External (customer-facing) status */}
                  <label className="flex flex-col gap-1">
                    <span className="text-xs font-medium text-gray-600 dark:text-gray-400">
                      สถานะที่ลูกค้าเห็น
                    </span>
                    <select
                      value={ticket.status}
                      disabled={externalStatusMutation.isPending}
                      onChange={(e) => externalStatusMutation.mutate(e.target.value)}
                      className="rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-blue-400 focus:outline-none disabled:opacity-50 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100"
                    >
                      {TICKET_STATUSES.map((s) => (
                        <option key={s} value={s}>
                          {statusLabel(t, s)}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>
                {/* Reply AS the Zaviago Support Team (never John's name). */}
                <div className="space-y-2 border-t border-blue-200/70 pt-3 dark:border-blue-500/20">
                  <p className="text-xs font-medium text-gray-600 dark:text-gray-400">
                    ตอบกลับในนาม <span className="text-blue-700 dark:text-blue-300">ทีมงาน Zaviago</span>
                  </p>
                  <RichComposer
                    key={composerKey}
                    minHeightClass="min-h-[72px]"
                    placeholder="เขียนข้อความตอบกลับถึงลูกค้า…"
                    onChange={(html, text) => {
                      setReplyHtml(html);
                      setReplyText(text);
                    }}
                  />
                  <div className="flex items-center justify-end">
                    <Button
                      className="rounded-full"
                      size="sm"
                      onClick={() =>
                        replyText.trim() &&
                        !agentReplyMutation.isPending &&
                        agentReplyMutation.mutate({ text: replyText, html: replyHtml || undefined })
                      }
                      disabled={!replyText.trim() || agentReplyMutation.isPending}
                    >
                      {agentReplyMutation.isPending ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Send className="h-4 w-4" />
                      )}
                      ส่งในนามทีมงาน
                    </Button>
                  </div>
                </div>
              </div>
            ) : (
            <div className="sticky bottom-0 mt-4 space-y-2 bg-white pb-1 dark:bg-gray-950">
              {/* Quick replies */}
              <div className="flex flex-wrap gap-1.5">
                {[t.quickNotFixed, t.quickThanks, t.quickMoreInfo].map((label) => (
                  <button
                    key={label}
                    onClick={() => sendQuick(label)}
                    disabled={replyMutation.isPending}
                    className="rounded-full border border-gray-200 bg-white px-3 py-1 text-xs font-medium text-gray-600 transition-colors hover:border-gray-300 hover:bg-gray-50 disabled:opacity-50 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-300 dark:hover:bg-gray-800"
                  >
                    {label}
                  </button>
                ))}
              </div>

              {/* Quote-reply bar */}
              {replyingTo && (
                <div className="flex items-start gap-2 rounded-lg border-l-2 border-blue-400 bg-blue-50/60 px-3 py-2 text-xs dark:bg-blue-500/10">
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-primary dark:text-primary">
                      {t.replyingTo}{" "}
                      {replyingTo.senderType === "agent" ? t.agentName : replyingTo.senderName}
                    </p>
                    <p className="truncate text-gray-500 dark:text-gray-400">{snippetOf(replyingTo)}</p>
                  </div>
                  <button
                    onClick={() => setReplyingTo(null)}
                    className="shrink-0 rounded-full p-0.5 text-gray-400 hover:bg-gray-200/60 hover:text-gray-700 dark:hover:bg-gray-700"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              )}

              <RichComposer
                key={composerKey}
                minHeightClass="min-h-[84px]"
                placeholder={t.replyPlaceholder}
                onChange={(html, text) => {
                  setReplyHtml(html);
                  setReplyText(text);
                }}
              />
              <div className="flex items-center justify-between">
                <span className="text-[11px] text-gray-400 dark:text-gray-500">
                  {t.estResponse.replace("{{mins}}", String(EST_RESPONSE_MINS))}
                </span>
                <Button className="rounded-full" size="sm" onClick={sendRich} disabled={!canSend}>
                  {replyMutation.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Send className="h-4 w-4" />
                  )}
                  {t.send}
                </Button>
              </div>
            </div>
            )}
              </div>
            </div>
          </>
        )}
      </PageContainer>

      {lightboxSrc && <ImageLightbox src={lightboxSrc} onClose={() => setLightboxSrc(null)} />}
    </div>
  );
}

interface MediaItem {
  src: string;
  kind: "image" | "video";
  /** Who sent it: the customer (sender) or the Zaviago team (receiver). */
  from: "customer" | "agent";
}

/** One thumbnail — image opens the lightbox, video opens in a new tab. */
function MediaThumb({ m, onOpenImage }: { m: MediaItem; onOpenImage: (src: string) => void }) {
  if (m.kind === "video") {
    return (
      <a
        href={m.src}
        target="_blank"
        rel="noreferrer"
        title="เปิดวิดีโอ"
        className="group relative aspect-square overflow-hidden rounded-lg border border-gray-200 bg-gray-100 dark:border-gray-700 dark:bg-gray-800"
      >
        <video src={m.src} muted playsInline preload="metadata" className="h-full w-full object-cover" />
        <span className="absolute inset-0 flex items-center justify-center bg-black/25 transition-colors group-hover:bg-black/40">
          <Play className="h-4 w-4 fill-white text-white" />
        </span>
      </a>
    );
  }
  return (
    <button
      type="button"
      onClick={() => onOpenImage(m.src)}
      title="ดูรูปภาพ"
      className="aspect-square overflow-hidden rounded-lg border border-gray-200 bg-gray-100 transition-opacity hover:opacity-80 dark:border-gray-700 dark:bg-gray-800"
    >
      <img src={m.src} alt="" loading="lazy" className="h-full w-full object-cover" />
    </button>
  );
}

/** Sidebar media gallery — thumbnails of every image/video in the ticket,
 *  grouped by sender (customer) vs receiver (Zaviago team). Tap to preview. */
function TicketMediaGallery({
  items,
  onOpenImage,
}: {
  items: MediaItem[];
  onOpenImage: (src: string) => void;
}) {
  if (items.length === 0) return null;
  const groups = [
    { key: "customer", label: "จากลูกค้า", items: items.filter((m) => m.from === "customer") },
    { key: "agent", label: "จากทีมงาน", items: items.filter((m) => m.from === "agent") },
  ].filter((g) => g.items.length > 0);
  return (
    <div className="mt-4 border-t border-gray-100 pt-4 dark:border-gray-800">
      <div className="mb-2 flex items-center gap-1.5 text-xs font-medium text-gray-500 dark:text-gray-400">
        <Images className="h-3.5 w-3.5 shrink-0 text-primary" />
        สื่อในทิกเก็ต
        <span className="ml-auto tabular-nums text-gray-400 dark:text-gray-500">{items.length}</span>
      </div>
      <div className="space-y-3">
        {groups.map((g) => (
          <div key={g.key}>
            <p className="mb-1.5 flex items-center gap-1.5 text-[11px] font-medium text-gray-400 dark:text-gray-500">
              {g.label}
              <span className="tabular-nums">· {g.items.length}</span>
            </p>
            <div className="grid grid-cols-4 gap-1.5">
              {g.items.map((m, i) => (
                <MediaThumb key={`${m.src}-${i}`} m={m} onOpenImage={onOpenImage} />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function SystemPill({ children, tone = "muted" }: { children: ReactNode; tone?: "muted" | "success" }) {
  return (
    <div className="flex justify-center">
      <span
        className={cn(
          "inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium",
          tone === "success"
            ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300"
            : "bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400",
        )}
      >
        {children}
      </span>
    </div>
  );
}

function TypingIndicator({ label }: { label: string }) {
  return (
    <div className="flex gap-3 justify-start">
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-semibold text-white">
        Z
      </div>
      <div className="flex items-center gap-2 rounded-2xl rounded-tl-sm bg-gray-100 px-4 py-3 dark:bg-gray-800">
        <span className="flex gap-1">
          {[0, 150, 300].map((delay) => (
            <span
              key={delay}
              className="h-1.5 w-1.5 animate-bounce rounded-full bg-gray-400 dark:bg-gray-500"
              style={{ animationDelay: `${delay}ms` }}
            />
          ))}
        </span>
        <span className="text-xs text-gray-500 dark:text-gray-400">{label}</span>
      </div>
    </div>
  );
}

// Inline editor for an agent message (receiver/john only). Reuses RichComposer
// seeded with the existing HTML so John keeps full formatting + inline media.
function MessageEditor({
  message,
  onChange,
  onSave,
  onCancel,
  canSave,
  saving,
}: {
  message: TicketMessage;
  onChange: (html: string, text: string) => void;
  onSave: () => void;
  onCancel: () => void;
  canSave: boolean;
  saving: boolean;
}) {
  return (
    <div className="flex justify-start">
      <div className="w-full max-w-[78%] space-y-2 rounded-2xl border border-blue-200 bg-blue-50/50 p-3 dark:border-blue-500/30 dark:bg-blue-500/5">
        <p className="px-1 text-xs font-medium text-blue-700 dark:text-blue-300">
          แก้ไขข้อความทีมงาน
        </p>
        <RichComposer
          minHeightClass="min-h-[72px]"
          placeholder="แก้ไขข้อความ…"
          initialHtml={message.bodyHtml || message.body || ""}
          onChange={onChange}
        />
        <div className="flex items-center justify-end gap-2">
          <Button variant="outline" size="sm" className="rounded-full" onClick={onCancel} disabled={saving}>
            <X className="h-3.5 w-3.5" />
            ยกเลิก
          </Button>
          <Button size="sm" className="rounded-full" onClick={onSave} disabled={!canSave || saving}>
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5" />}
            บันทึก
          </Button>
        </div>
      </div>
    </div>
  );
}

function MessageBubble({
  message,
  t,
  language,
  onReply,
  onImageClick,
  canModerate = false,
  onEdit,
  onDelete,
  deleting = false,
}: {
  message: TicketMessage;
  t: T;
  language: string;
  onReply: () => void;
  onImageClick: (src: string) => void;
  /** Receiver-only (john): show inline edit/delete affordances. */
  canModerate?: boolean;
  onEdit?: () => void;
  onDelete?: () => void;
  deleting?: boolean;
}) {
  const isAgent = message.senderType === "agent";
  const bubbleTone = isAgent
    ? "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-100"
    // Customer (sent) bubble: soft Google-Chat blue container instead of the bold
    // brand primary — reads calmer and pairs with the neutral agent bubble.
    : "bg-[#d3e3fd] text-gray-900 dark:bg-[#004a77] dark:text-blue-50";

  const handleImgClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const el = e.target as HTMLElement;
    if (el.tagName === "IMG") onImageClick((el as HTMLImageElement).currentSrc || (el as HTMLImageElement).src);
  };

  return (
    <div className={cn("group flex gap-3", isAgent ? "justify-start" : "justify-end")}>
      {isAgent && (
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-semibold text-white">
          Z
        </div>
      )}
      <div className={cn("max-w-[78%]", message.pending && "opacity-70")}>
        <div className={cn("flex items-center gap-2 px-1 pb-1 text-xs", isAgent ? "justify-start" : "justify-end")}>
          {/* Reply affordance (appears on hover) */}
          {!isAgent && !message.pending && (
            <button
              onClick={onReply}
              className="flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-gray-400 opacity-0 transition-opacity hover:text-gray-700 group-hover:opacity-100 dark:hover:text-gray-200"
              title={t.replyToMessage}
            >
              <Reply className="h-3 w-3" />
            </button>
          )}
          <span className="font-medium text-gray-700 dark:text-gray-300">
            {isAgent ? t.agentName : message.senderName}
          </span>
          <span className="text-gray-400 dark:text-gray-500">
            {format(new Date(message.sentOn), "HH:mm", { locale: language === "th" ? th : undefined })}
          </span>
          {isAgent && (
            <button
              onClick={onReply}
              className="flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-gray-400 opacity-0 transition-opacity hover:text-gray-700 group-hover:opacity-100 dark:hover:text-gray-200"
              title={t.replyToMessage}
            >
              <Reply className="h-3 w-3" />
            </button>
          )}
          {/* Receiver-only (john) moderation — ALWAYS VISIBLE (not hover-gated).
              Hover-only icons were undiscoverable on trackpad and never showed on
              touch/tablet, so John couldn't find edit/delete. These are persistent,
              labelled, tappable pills gated to the support receiver only. */}
          {canModerate && !message.pending && (
            <span className="inline-flex items-center gap-1">
              {isAgent && onEdit && (
                <button
                  onClick={onEdit}
                  className="inline-flex items-center gap-1 rounded-full border border-gray-200 bg-white px-2 py-0.5 text-[11px] font-medium text-gray-600 shadow-sm transition-colors hover:border-gray-300 hover:bg-gray-50 hover:text-gray-900 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700 dark:hover:text-white"
                  title="แก้ไขข้อความทีมงาน"
                >
                  <Pencil className="h-3 w-3" />
                  แก้ไข
                </button>
              )}
              {onDelete && (
                <button
                  onClick={onDelete}
                  disabled={deleting}
                  className="inline-flex items-center gap-1 rounded-full border border-gray-200 bg-white px-2 py-0.5 text-[11px] font-medium text-gray-600 shadow-sm transition-colors hover:border-red-200 hover:bg-red-50 hover:text-red-600 disabled:opacity-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:border-red-500/40 dark:hover:bg-red-500/10 dark:hover:text-red-400"
                  title="ลบข้อความ"
                >
                  <Trash2 className="h-3 w-3" />
                  ลบ
                </button>
              )}
            </span>
          )}
        </div>

        {/* Quoted message this is replying to */}
        {message.replyTo && (
          <div
            className={cn(
              "mb-1 rounded-lg border-l-2 px-2.5 py-1 text-xs",
              isAgent
                ? "border-gray-300 bg-gray-50 text-gray-500 dark:border-gray-600 dark:bg-gray-800/60 dark:text-gray-400"
                : "border-blue-400/60 bg-blue-500/10 text-gray-600 dark:border-blue-300/40 dark:bg-blue-400/10 dark:text-blue-100",
            )}
          >
            <span className="font-medium">
              {message.replyTo.senderType === "agent" ? t.agentName : message.senderName}
            </span>
            <span className="ml-1 line-clamp-1">{message.replyTo.snippet}</span>
          </div>
        )}

        {message.bodyHtml ? (
          (() => {
            const { textHtml, mediaHtml, hasText } = splitMessageMedia(
              sanitizeHtml(resolveMediaUrls(message.bodyHtml)),
            );
            return (
              <div className={cn("flex flex-col gap-1.5", isAgent ? "items-start" : "items-end")}>
                {hasText && (
                  <div
                    className={cn(
                      "rounded-2xl px-4 py-2.5 text-sm leading-relaxed [&_ol]:list-decimal [&_ol]:pl-5 [&_ul]:list-disc [&_ul]:pl-5",
                      isAgent ? "rounded-tl-sm" : "rounded-tr-sm",
                      bubbleTone,
                    )}
                    dangerouslySetInnerHTML={{ __html: textHtml }}
                  />
                )}
                {/* Media floats free of the bubble — just rounded, zoomable media. */}
                {mediaHtml.map((m, i) => (
                  <div
                    key={i}
                    onClick={handleImgClick}
                    className="overflow-hidden rounded-2xl [&_img]:block [&_img]:max-h-96 [&_img]:max-w-full [&_img]:cursor-zoom-in [&_video]:block [&_video]:max-h-96 [&_video]:max-w-full"
                    dangerouslySetInnerHTML={{ __html: m }}
                  />
                ))}
              </div>
            );
          })()
        ) : (
          <div
            className={cn(
              "whitespace-pre-wrap rounded-2xl px-4 py-2.5 text-sm leading-relaxed",
              isAgent ? "rounded-tl-sm" : "rounded-tr-sm",
              bubbleTone,
            )}
          >
            {message.body}
          </div>
        )}
      </div>
      {!isAgent && (
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gray-200 text-xs font-semibold text-gray-600 dark:bg-gray-700 dark:text-gray-200">
          {message.senderName.slice(0, 2).toUpperCase()}
        </div>
      )}
    </div>
  );
}
